import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class RiskConvergenceService {
  constructor(private readonly http: HttpClient) { }

  projectList() {
    return this.http.get("delivery/project-list?flag=mapped_list");
  }

  getVendorList() {
    return this.http.get(
      "dynamic-form/?form_name=opportunity_empanelled_vendors"
    );
  }

  getRiskFunctionOwners(functionId: number) {
    return this.http.get(
      `risk/risks/function-owners/?function_id=${functionId}`
    );
  }

  getRiskMaster(filterString = "", moduleName: string = '') {
    return this.http.get(`risk/risks/?${filterString}` + `&module=${moduleName}`);
  }

  getRiskById(id: any) {
    return this.http.get(`risk/risks/${id}/`);
  }

  getRiskProject(queryParam: any, moduleName: string = '') {
    return this.http.get(`delivery/get-risk-goal-project/?${queryParam}&module=${moduleName}`);
  }

  getRiskApplication(queryParam: any) {
    return this.http.get(`master/application/list-unique/?${queryParam}`);
  }

  updateRisk(payload: any) {
    return this.http.post(`risk/risks/`, payload);
  }

  moveRisks(payload: { risk_ids: number[]; target_domain_id: number; target_subdomain_id: number }) {
    return this.http.post(`risk/risks/move/`, payload);
  }

  getRiskTracker(offSet?: any, end?: any, moduleName: string = '') {
    const params = `offset=${offSet}&index=${end}&cycle=active&module=${moduleName}`;
    return this.http.get(`risk/project-risks/?${params}`);
  }

  getFilteredRiskTracker(offSet?: any, end?: any, queryParams: string = "", moduleName: string = '') {
    let params = "";
    const cycleParam = queryParams.includes("cycle=") ? "" : "&cycle=active";
    if (offSet !== undefined && end !== undefined && offSet !== null && end !== null) {
      params = `offset=${offSet}&index=${end}${queryParams}${cycleParam}&module=${moduleName}`;
    } else {
      const base = queryParams.startsWith("&") ? queryParams.substring(1) : queryParams;
      params = `${base}${cycleParam}&module=${moduleName}`;
    }
    return this.http.get(`risk/project-risks/?${params}`);
  }

  downloadTrackerList(queryParam: any) {
    const cycleParam = (queryParam && queryParam.includes("cycle=")) ? "" : "&cycle=active";
    return this.http.get(`risk/project-risks/?response_type=csv${queryParam}${cycleParam}`, {
      responseType: "blob",
    });
  }

  deleteFile(fileId: number) {
    return this.http.delete(`risk/project-risks/delete-file?file_id=${fileId}`);
  }

  downloadFile(fileId: number) {
    return this.http.post(`risk/project-risks/download-file/${fileId}`, {
      responseType: "blob",
    });
  }

  getRiskPulse() {
    return this.http.get("nps/pulses/?performance_impact=Risk");
  }

  getRiskImpactList() {
    return this.http.get("risk/project-risks/risk-impact");
  }

  getRiskLikelihoodList() {
    return this.http.get("risk/project-risks/risk-likelihood");
  }

  getAssetImpactList() {
    return this.http.get("risk/project-risks/asset-impact");
  }

  getRiskScoreRange() {
    return this.http.get("risk/project-risks/risk-score-range");
  }

  getRiskMatrixConfig() {
    return this.http.get("risk/project-risks/risk-matrix-configuration");
  }

  getRiskScoreColorFromConfig(score: any, config?: any[]): string {
    const num = Number(score);
    if (!score || isNaN(num) || num === 0) return "#bebebf";

    if (config && config.length) {
      const match = config.find(
        (c: any) => num >= Number(c.min_score) && num <= Number(c.max_score)
      );
      if (match && match.color) {
        return this.resolveColor(match.color);
      }

      // If the score goes beyond the max score of high or whatever the last order is, use that color
      const sorted = [...config].sort(
        (a: any, b: any) => Number(a.order ?? a.max_score ?? 0) - Number(b.order ?? b.max_score ?? 0)
      );
      const highestBand = sorted[sorted.length - 1];
      if (highestBand && num > Number(highestBand.max_score)) {
        return this.resolveColor(highestBand.color);
      }
      const lowestBand = sorted[0];
      if (lowestBand && num < Number(lowestBand.min_score) && num > 0) {
        return this.resolveColor(lowestBand.color);
      }
    }

    if (num <= 5) return "#16a34a";
    if (num <= 14) return "#f59e0b";
    return "#dc2626";
  }

  resolveColor(color: string): string {
    if (!color) return "#bebebf";
    const c = color.toLowerCase().trim();
    const colorMap: Record<string, string> = {
      green: "#16a34a",
      amber: "#f59e0b",
      orange: "#f59e0b",
      red: "#dc2626",
      rose: "#e11d48",
      teal: "#0bb797",
      grey: "#bebebf",
      gray: "#bebebf",
    };
    return colorMap[c] || color;
  }

  getScoreTooltip(score: any, config?: any[]): string {
    const num = Number(score);
    if (!score || isNaN(num) || num === 0) return "0";
    if (config && config.length) {
      const match = config.find(
        (c: any) => num >= Number(c.min_score) && num <= Number(c.max_score)
      );
      if (match && match.label) {
        return `${match.label}: ${num}`;
      }
      const sorted = [...config].sort(
        (a: any, b: any) => Number(a.order ?? a.max_score ?? 0) - Number(b.order ?? b.max_score ?? 0)
      );
      const highestBand = sorted[sorted.length - 1];
      if (highestBand && num > Number(highestBand.max_score)) {
        return `${highestBand.label || 'High'}: ${num}`;
      }
      const lowestBand = sorted[0];
      if (lowestBand && num < Number(lowestBand.min_score) && num > 0) {
        return `${lowestBand.label || 'Low'}: ${num}`;
      }
    }
    return String(num);
  }


  getActionAgeingRange() {
    return this.http.get("risk/project-risks/action-ageing");
  }

  getRiskStatusList() {
    return this.http.get("risk/project-risks/risk-status");
  }

  getRiskControlTypes() {
    return this.http.get("risk/project-risks/risk-control-types");
  }

  getRiskTypes() {
    return this.http.get("risk/risks/risk-types");
  }

  getRiskControls(riskId: number) {
    return this.http.get(`risk/project-risks/${riskId}/risk-controls/`);
  }

  getRiskMatrixData(projectId: any, cycleId: any = null) {
    let url = `risk/project-risks/${projectId}/risk-matrix/`;
    if (cycleId) {
      url += `?cycle=${cycleId}`;
    }
    return this.http.get(url);
  }

  getRiskHistory(riskId: number) {
    return this.http.get(`risk/project-risks/${riskId}/history`)
  }

  getRiskPrefix() {
    return this.http.get(`risk/risks/company-prefix`)
  }
}
